import { useEffect, useState, useCallback } from 'react';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import CashFlowEntryModal from './components/CashFlowEntryModal';
import CashFlowReports from './components/CashFlowReports';
import CashFlowTable from './components/CashFlowTable';

export interface CashFlowEntry {
  id: string;
  entry_id: string | null;
  project_id: string | null;
  client_id: string | null;
  task_id: string | null;
  flow_type: 'credit' | 'debit';
  origin: string;
  amount: number;
  description: string | null;
  payment_method: string | null;
  reference_date: string;
  created_by: string | null;
  created_at: string;
  // joins
  _project_name?: string;
  _client_name?: string;
  _task_title?: string;
  _created_by_name?: string;
}

type ActiveTab = 'extrato' | 'relatorios';

export default function FinanceiroPage() {
  const { profile } = useAuthContext();

  const [entries, setEntries] = useState<CashFlowEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>('extrato');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'credit' | 'debit'>('credit');

  // Filtros do extrato
  const [filterType, setFilterType] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  // Listas para filtros
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);

    const [cashRes, clientsRes, projectsRes] = await Promise.all([
      supabase
        .from('cash_flow')
        .select('*')
        .order('reference_date', { ascending: false })
        .order('created_at', { ascending: false }),
      supabase.from('clients').select('id, name').eq('is_active', true).order('name'),
      supabase.from('projects').select('id, name').order('name'),
    ]);

    const rawEntries: CashFlowEntry[] = cashRes.data || [];

    // Enriquecer com nomes via joins manuais
    const projectIds = [...new Set(rawEntries.map(e => e.project_id).filter(Boolean))];
    const clientIds = [...new Set(rawEntries.map(e => e.client_id).filter(Boolean))];
    const taskIds = [...new Set(rawEntries.map(e => e.task_id).filter(Boolean))];
    const userIds = [...new Set(rawEntries.map(e => e.created_by).filter(Boolean))];

    const [projMap, clientMap, taskMap, userMap] = await Promise.all([
      projectIds.length > 0
        ? supabase.from('projects').select('id, name').in('id', projectIds as string[])
        : { data: [] },
      clientIds.length > 0
        ? supabase.from('clients').select('id, name').in('id', clientIds as string[])
        : { data: [] },
      taskIds.length > 0
        ? supabase.from('tasks').select('id, title').in('id', taskIds as string[])
        : { data: [] },
      userIds.length > 0
        ? supabase.from('profiles').select('id, full_name').in('id', userIds as string[])
        : { data: [] },
    ]);

    const pMap = Object.fromEntries((projMap.data || []).map((p: { id: string; name: string }) => [p.id, p.name]));
    const cMap = Object.fromEntries((clientMap.data || []).map((c: { id: string; name: string }) => [c.id, c.name]));
    const tMap = Object.fromEntries((taskMap.data || []).map((t: { id: string; title: string }) => [t.id, t.title]));
    const uMap = Object.fromEntries((userMap.data || []).map((u: { id: string; full_name: string }) => [u.id, u.full_name]));

    const enriched = rawEntries.map(e => ({
      ...e,
      _project_name: e.project_id ? pMap[e.project_id] || '—' : '—',
      _client_name: e.client_id ? cMap[e.client_id] || '—' : '—',
      _task_title: e.task_id ? tMap[e.task_id] || null : null,
      _created_by_name: e.created_by ? uMap[e.created_by] || '—' : '—',
    }));

    setEntries(enriched);
    setClients(clientsRes.data || []);
    setProjects(projectsRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = entries.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || (e.description || '').toLowerCase().includes(q)
      || (e._client_name || '').toLowerCase().includes(q)
      || (e._project_name || '').toLowerCase().includes(q);
    const matchType = !filterType || e.flow_type === filterType;
    const matchClient = !filterClient || e.client_id === filterClient;
    const matchProject = !filterProject || e.project_id === filterProject;
    const matchFrom = !dateFrom || e.reference_date >= dateFrom;
    const matchTo = !dateTo || e.reference_date <= dateTo;
    return matchSearch && matchType && matchClient && matchProject && matchFrom && matchTo;
  });

  const totalCredits = filtered.filter(e => e.flow_type === 'credit').reduce((s, e) => s + Number(e.amount), 0);
  const totalDebits = filtered.filter(e => e.flow_type === 'debit').reduce((s, e) => s + Number(e.amount), 0);
  const balance = totalCredits - totalDebits;

  // Saldo geral (sem filtros)
  const globalCredits = entries.filter(e => e.flow_type === 'credit').reduce((s, e) => s + Number(e.amount), 0);
  const globalDebits = entries.filter(e => e.flow_type === 'debit').reduce((s, e) => s + Number(e.amount), 0);
  const globalBalance = globalCredits - globalDebits;

  const openCredit = () => { setModalType('credit'); setModalOpen(true); };
  const openDebit = () => { setModalType('debit'); setModalOpen(true); };

  const handleSaveEntry = async (data: {
    flow_type: 'credit' | 'debit';
    origin: string;
    client_id: string | null;
    project_id: string | null;
    task_id: string | null;
    amount: number;
    reference_date: string;
    description: string;
    payment_method: string;
    notes: string;
  }) => {
    const { notes: _notes, ...insertData } = data;
    const { error } = await supabase.from('cash_flow').insert({
      ...insertData,
      created_by: profile?.id,
    });
    if (!error) {
      setModalOpen(false);
      loadData();
    }
  };

  const handleDelete = async (id: string) => {
    await supabase.from('cash_flow').delete().eq('id', id);
    loadData();
  };

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-slate-800 text-xl font-bold">Caixa</h1>
          <p className="text-slate-400 text-xs mt-0.5">Livro-caixa com créditos, débitos e relatórios</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openDebit}
            className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-down-circle-line"></i>
            Débito
          </button>
          <button
            onClick={openCredit}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-up-circle-line"></i>
            Crédito
          </button>
        </div>
      </div>

      {/* Cards de Saldo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={`rounded-xl border p-5 ${globalBalance >= 0 ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wide">Saldo Atual</p>
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${globalBalance >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <i className={`text-base ${globalBalance >= 0 ? 'ri-safe-line text-emerald-600' : 'ri-error-warning-line text-red-600'}`}></i>
            </div>
          </div>
          <p className={`text-2xl font-bold ${globalBalance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
            {fmt(globalBalance)}
          </p>
          <p className="text-slate-400 text-xs mt-1">{entries.length} lançamentos no total</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wide">Total Créditos</p>
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-100">
              <i className="ri-arrow-up-circle-line text-emerald-600 text-base"></i>
            </div>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{fmt(globalCredits)}</p>
          <p className="text-slate-400 text-xs mt-1">{entries.filter(e => e.flow_type === 'credit').length} entradas</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wide">Total Débitos</p>
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-100">
              <i className="ri-arrow-down-circle-line text-red-500 text-base"></i>
            </div>
          </div>
          <p className="text-2xl font-bold text-red-500">{fmt(globalDebits)}</p>
          <p className="text-slate-400 text-xs mt-1">{entries.filter(e => e.flow_type === 'debit').length} saídas</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit mb-6">
        {(['extrato', 'relatorios'] as ActiveTab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab === 'extrato' ? 'Extrato do Caixa' : 'Relatórios'}
          </button>
        ))}
      </div>

      {activeTab === 'extrato' && (
        <CashFlowTable
          entries={filtered}
          loading={loading}
          search={search}
          filterType={filterType}
          filterClient={filterClient}
          filterProject={filterProject}
          dateFrom={dateFrom}
          dateTo={dateTo}
          clients={clients}
          projects={projects}
          totalCredits={totalCredits}
          totalDebits={totalDebits}
          balance={balance}
          onSearchChange={setSearch}
          onFilterTypeChange={setFilterType}
          onFilterClientChange={setFilterClient}
          onFilterProjectChange={setFilterProject}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onDelete={handleDelete}
          onOpenCredit={openCredit}
        />
      )}

      {activeTab === 'relatorios' && (
        <CashFlowReports entries={entries} clients={clients} projects={projects} />
      )}

      {modalOpen && (
        <CashFlowEntryModal
          type={modalType}
          clients={clients}
          projects={projects}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveEntry}
        />
      )}
    </AppLayout>
  );
}